/**
 * `TypingSession` — 1プレイぶんの状態機械(docs/03_architecture.md §3.2)。
 *
 * 入力はメソッド呼び出し、出力はイベントと状態スナップショットのみ。
 * DOM / Phaser / タイマーAPI に一切触らないので、時刻はすべて呼び出し側が渡す。
 */

import { QUIZ_DEFAULTS, getDifficultySettings, type DifficultySettings } from './config.js';
import {
  NO_MODIFIERS,
  resolveModifiers,
  type ResolvedModifiers,
  type ScoreModifier,
} from './modifiers.js';
import {
  calcClearCoins,
  calcComboBonus,
  calcGaugeMultiplier,
  calcKeyScore,
  calcKpm,
  calcMissRate,
  calcPerformanceStage,
  calcStars,
  decayGauge,
  gainGauge,
} from './scoring.js';
import type { InputValidator, WordMatcher } from './validators.js';
import type {
  Difficulty,
  PerformanceStage,
  QuizConfig,
  SessionEndReason,
  SessionPhase,
  StageWord,
  TypingLanguage,
} from './types.js';

/** セッション生成オプション。 */
export interface TypingSessionOptions {
  readonly words: readonly StageWord[];
  readonly validator: InputValidator;
  readonly difficulty: Difficulty;
  /**
   * ステージの言語(docs/02 §5.7 のコイン係数が言語別)。既定は `japanese`。
   * 表示用の見積りにしか使わない(コインの正はサーバー)。
   */
  readonly language?: TypingLanguage;
  /** 装備アイテム効果。 */
  readonly modifiers?: readonly ScoreModifier[];
  /** 出題設定。既定は `sequential` × 1周。 */
  readonly quiz?: QuizConfig;
  /** 乱数生成器(テスト用に差し替え可能)。既定 `Math.random`。 */
  readonly random?: () => number;
  /** 開始時刻(ms)。既定 0。エンジンは時計を持たない。 */
  readonly startedAt?: number;
}

/** 状態スナップショット。`getState()` が毎回新しいオブジェクトを返す。 */
export interface SessionState {
  readonly phase: SessionPhase;
  readonly difficulty: Difficulty;
  readonly score: number;
  readonly combo: number;
  readonly maxCombo: number;
  readonly comboBonus: number;
  readonly gauge: number;
  readonly gaugeMultiplier: number;
  readonly performanceStage: PerformanceStage;
  /** 現在の問題番号(0始まり)。 */
  readonly questionIndex: number;
  /** 総問題数。timeLimit モードでは null。 */
  readonly totalQuestions: number | null;
  /** 完了した問題数。 */
  readonly completedQuestions: number;
  readonly word: StageWord | null;
  /** ガイド表示用の全体ローマ字/綴り。 */
  readonly guide: string;
  readonly typed: string;
  readonly remaining: string;
  /** 次に受理されるキー集合(UIのキーボード強調用)。 */
  readonly nextKeys: readonly string[];
  /** 読みの何文字目まで確定したか。 */
  readonly unitIndex: number;
  readonly correctKeys: number;
  readonly missKeys: number;
  readonly totalKeys: number;
  readonly accuracy: number;
  readonly elapsedMs: number;
  /** 制限時間モードの残り時間(ms)。それ以外は null。 */
  readonly remainingMs: number | null;
  /** 最後に受理された打鍵からの経過時間(ms)。ヒント再表示の判定に使う。 */
  readonly idleMs: number;
  /** 残りのミス見逃し回数。 */
  readonly missForgiveRemaining: number;
}

/** リザルト。 */
export interface SessionResult {
  readonly difficulty: Difficulty;
  readonly score: number;
  readonly maxCombo: number;
  readonly correctKeys: number;
  readonly missKeys: number;
  readonly totalKeys: number;
  /** 正確率(0〜1)。 */
  readonly accuracy: number;
  /** ミス率(0〜1)。 */
  readonly missRate: number;
  /** 打鍵速度(正解打/分)。 */
  readonly kpm: number;
  readonly durationMs: number;
  readonly completedQuestions: number;
  readonly totalQuestions: number | null;
  readonly cleared: boolean;
  readonly stars: number;
  /** 通常クリアぶんのコイン(初回星ボーナスはサーバ側で加算)。 */
  readonly coins: number;
  readonly endReason: SessionEndReason;
}

/** セッションが発火するイベント。 */
export type SessionEvent =
  | { readonly type: 'sessionStart'; readonly at: number; readonly totalQuestions: number | null }
  | {
      readonly type: 'wordStart';
      readonly at: number;
      readonly index: number;
      readonly word: StageWord;
      readonly guide: string;
    }
  | {
      readonly type: 'keyAccepted';
      readonly at: number;
      readonly key: string;
      readonly gain: number;
      readonly score: number;
      readonly combo: number;
      readonly gauge: number;
      readonly typed: string;
      readonly remaining: string;
    }
  | {
      readonly type: 'keyRejected';
      readonly at: number;
      readonly key: string;
      readonly expected: readonly string[];
      /** アイテム効果でコンボ・ゲージが維持されたか。 */
      readonly forgiven: boolean;
    }
  | {
      readonly type: 'wordCompleted';
      readonly at: number;
      readonly index: number;
      readonly word: StageWord;
    }
  | {
      readonly type: 'comboChanged';
      readonly at: number;
      readonly combo: number;
      readonly bonus: number;
    }
  | {
      readonly type: 'gaugeChanged';
      readonly at: number;
      readonly gauge: number;
      readonly multiplier: number;
    }
  | { readonly type: 'stageChanged'; readonly at: number; readonly stage: PerformanceStage }
  | { readonly type: 'feverStart'; readonly at: number }
  | { readonly type: 'feverEnd'; readonly at: number }
  | { readonly type: 'sessionEnd'; readonly at: number; readonly result: SessionResult };

/** イベントリスナ。 */
export type SessionEventListener = (event: SessionEvent) => void;

const DEFAULT_QUIZ: QuizConfig = { mode: 'sequential' };

export class TypingSession {
  private readonly words: readonly StageWord[];
  private readonly validator: InputValidator;
  private readonly quiz: QuizConfig;
  private readonly random: () => number;
  private readonly mods: ResolvedModifiers;

  private readonly listeners = new Set<SessionEventListener>();

  private phase: SessionPhase = 'idle';
  private readonly difficulty: Difficulty;
  private readonly language: TypingLanguage;

  private plan: number[] = [];
  private questionIndex = 0;
  private completedQuestions = 0;
  /** 出題中の1問(未出題・終了後は null)。 */
  private current: { readonly word: StageWord; readonly matcher: WordMatcher } | null = null;

  private score = 0;
  private combo = 0;
  private maxCombo = 0;
  private gauge = 0;
  private stage: PerformanceStage = 'normal';
  private correctKeys = 0;
  private missKeys = 0;
  private forgiveRemaining = 0;

  private startedAt: number;
  private now: number;
  private lastInputAt: number;
  private endedAt: number | null = null;
  private endReason: SessionEndReason = 'completed';
  private result: SessionResult | null = null;

  constructor(options: TypingSessionOptions) {
    this.words = options.words;
    this.validator = options.validator;
    this.difficulty = options.difficulty;
    this.language = options.language ?? 'japanese';
    this.quiz = options.quiz ?? DEFAULT_QUIZ;
    this.random = options.random ?? Math.random;
    this.mods = options.modifiers ? resolveModifiers(options.modifiers) : NO_MODIFIERS;
    this.forgiveRemaining = this.mods.missForgive;
    this.startedAt = options.startedAt ?? 0;
    this.now = this.startedAt;
    this.lastInputAt = this.startedAt;
    this.plan = this.buildPlan();
  }

  // ── イベント ───────────────────────────────────────────

  /** イベント購読。戻り値を呼ぶと解除できる。 */
  onEvent(listener: SessionEventListener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private emit(event: SessionEvent): void {
    for (const listener of [...this.listeners]) listener(event);
  }

  // ── ライフサイクル ─────────────────────────────────────

  /** セッションを開始する。既に開始済みなら何もしない。 */
  start(nowMs?: number): void {
    if (this.phase !== 'idle') return;
    const at = nowMs ?? this.startedAt;
    this.startedAt = at;
    this.now = at;
    this.lastInputAt = at;
    this.phase = 'playing';
    this.emit({ type: 'sessionStart', at, totalQuestions: this.totalQuestions() });
    if (this.plan.length === 0) {
      this.finish(at, 'completed');
      return;
    }
    this.loadQuestion(0, at);
  }

  /** セッションを中断する(クリア扱いにならない)。 */
  abort(nowMs?: number): void {
    if (this.phase !== 'playing') return;
    this.finish(nowMs ?? this.now, 'aborted');
  }

  /**
   * 時間経過を伝える(rAFから毎フレーム呼ぶ)。
   * ゲージ減衰と制限時間の判定を行う。
   */
  tick(nowMs: number): void {
    if (this.phase === 'idle') this.start(nowMs);
    if (this.phase !== 'playing') return;
    const delta = nowMs - this.now;
    if (delta > 0) {
      this.now = nowMs;
      this.applyDecay(delta, nowMs);
    } else {
      this.now = Math.max(this.now, nowMs);
    }

    const limitMs = this.timeLimitMs();
    if (limitMs !== null && this.now - this.startedAt >= limitMs) {
      this.finish(this.startedAt + limitMs, 'timeUp');
    }
  }

  /**
   * 1打鍵を入力する。受理できたら `true`。
   *
   * `Shift` などの複数文字キー名は無視され、ミスにもならない。
   * `timestampMs` を省略すると直近に渡された時刻を使う(エンジンは時計を持たない)。
   */
  input(key: string, timestampMs?: number): boolean {
    const at = timestampMs ?? this.now;
    if (this.phase === 'idle') this.start(at);
    if (timestampMs !== undefined) this.tick(timestampMs);
    if (this.phase !== 'playing') return false;
    if (key.length !== 1) return false;

    const current = this.current;
    if (!current) return false;

    if (current.matcher.input(key)) {
      this.onCorrect(key, at, current);
      return true;
    }
    this.onMiss(key, at, current);
    return false;
  }

  // ── 内部処理 ───────────────────────────────────────────

  private onCorrect(
    key: string,
    at: number,
    current: { readonly word: StageWord; readonly matcher: WordMatcher },
  ): void {
    this.correctKeys += 1;
    this.combo += 1;
    this.maxCombo = Math.max(this.maxCombo, this.combo);
    this.lastInputAt = at;

    const beforeGauge = this.gauge;
    this.gauge = gainGauge(this.gauge);

    const breakdown = calcKeyScore({
      totalCorrect: this.correctKeys,
      combo: this.combo,
      gauge: this.gauge,
      difficulty: this.difficulty,
      comboBonusCap: this.mods.comboBonusCap,
    });
    this.score += breakdown.gain;

    this.emit({
      type: 'keyAccepted',
      at,
      key: key.toLowerCase(),
      gain: breakdown.gain,
      score: this.score,
      combo: this.combo,
      gauge: this.gauge,
      typed: current.matcher.typed,
      remaining: current.matcher.remaining,
    });
    this.emit({ type: 'comboChanged', at, combo: this.combo, bonus: breakdown.comboBonus });
    if (this.gauge !== beforeGauge) this.emitGaugeChanged(at);
    this.syncStage(at);

    if (current.matcher.completed) this.completeWord(current.word, at);
  }

  private onMiss(
    key: string,
    at: number,
    current: { readonly word: StageWord; readonly matcher: WordMatcher },
  ): void {
    this.missKeys += 1;
    let forgiven = false;
    if (this.forgiveRemaining > 0) {
      this.forgiveRemaining -= 1;
      forgiven = true;
    } else {
      const hadCombo = this.combo !== 0;
      const hadGauge = this.gauge !== 0;
      this.combo = 0;
      this.gauge = 0;
      if (hadCombo) this.emit({ type: 'comboChanged', at, combo: 0, bonus: calcComboBonus(0) });
      if (hadGauge) this.emitGaugeChanged(at);
      this.syncStage(at);
    }
    this.emit({
      type: 'keyRejected',
      at,
      key: key.toLowerCase(),
      expected: current.matcher.nextKeys,
      forgiven,
    });
  }

  private completeWord(word: StageWord, at: number): void {
    this.completedQuestions += 1;
    this.emit({ type: 'wordCompleted', at, index: this.questionIndex, word });
    const next = this.questionIndex + 1;
    if (this.hasQuestion(next)) {
      this.loadQuestion(next, at);
    } else {
      this.finish(at, 'completed');
    }
  }

  private applyDecay(deltaMs: number, at: number): void {
    if (this.gauge <= 0) return;
    const before = this.gauge;
    this.gauge = decayGauge(this.gauge, deltaMs, this.difficulty, this.mods.gaugeDecayReduction);
    if (Math.floor(this.gauge) !== Math.floor(before) || this.gauge === 0) {
      this.emitGaugeChanged(at);
    }
    this.syncStage(at);
  }

  private emitGaugeChanged(at: number): void {
    this.emit({
      type: 'gaugeChanged',
      at,
      gauge: this.gauge,
      multiplier: calcGaugeMultiplier(this.gauge),
    });
  }

  private syncStage(at: number): void {
    const next = calcPerformanceStage(this.gauge);
    if (next === this.stage) return;
    const previous = this.stage;
    this.stage = next;
    this.emit({ type: 'stageChanged', at, stage: next });
    if (next === 'fever') this.emit({ type: 'feverStart', at });
    else if (previous === 'fever') this.emit({ type: 'feverEnd', at });
  }

  private loadQuestion(index: number, at: number): void {
    const wordIndex = this.plan[index] ?? 0;
    const word = this.words[wordIndex];
    /* v8 ignore next */
    if (!word) return;
    this.questionIndex = index;
    const matcher = this.validator.createMatcher(word.reading);
    this.current = { word, matcher };
    this.lastInputAt = at;
    this.emit({ type: 'wordStart', at, index, word, guide: matcher.guide });
  }

  private finish(at: number, reason: SessionEndReason): void {
    /* v8 ignore next */
    if (this.phase === 'ended') return;
    this.phase = 'ended';
    this.endedAt = at;
    this.endReason = reason;
    this.now = Math.max(this.now, at);
    this.result = this.buildResult();
    this.emit({ type: 'sessionEnd', at, result: this.result });
  }

  // ── 出題計画 ───────────────────────────────────────────

  private repeatCount(): number {
    return Math.max(1, Math.floor(this.quiz.repeatCount ?? QUIZ_DEFAULTS.repeatCount));
  }

  private timeLimitMs(): number | null {
    if (this.quiz.mode !== 'timeLimit') return null;
    return Math.max(0, this.quiz.timeLimit ?? QUIZ_DEFAULTS.timeLimitSec) * 1000;
  }

  private totalQuestions(): number | null {
    if (this.quiz.mode === 'timeLimit') return null;
    return this.plan.length;
  }

  private shuffled(): number[] {
    const indices = this.words.map((_, index) => index);
    for (let i = indices.length - 1; i > 0; i--) {
      const j = Math.floor(this.random() * (i + 1));
      const a = indices[i] ?? i;
      const b = indices[j] ?? j;
      indices[i] = b;
      indices[j] = a;
    }
    return indices;
  }

  private buildPlan(): number[] {
    if (this.words.length === 0) return [];
    switch (this.quiz.mode) {
      case 'sequential': {
        const plan: number[] = [];
        for (let round = 0; round < this.repeatCount(); round++) {
          for (let i = 0; i < this.words.length; i++) plan.push(i);
        }
        return plan;
      }
      case 'random': {
        const total = this.quiz.questionLimit ?? this.words.length * this.repeatCount();
        const plan: number[] = [];
        while (plan.length < total) plan.push(...this.shuffled());
        return plan.slice(0, Math.max(0, total));
      }
      case 'fixedQuestionCount': {
        const total = Math.max(0, this.quiz.questionLimit ?? this.words.length);
        const plan: number[] = [];
        for (let i = 0; i < total; i++) plan.push(i % this.words.length);
        return plan;
      }
      case 'timeLimit': {
        return this.shuffled();
      }
      default:
        // 未知のモード(コンテンツ側の先行追加)は全問1周にフォールバックする。
        return this.words.map((_, index) => index);
    }
  }

  /** timeLimit モードでは必要になった時点で計画を継ぎ足す。 */
  private hasQuestion(index: number): boolean {
    if (this.quiz.mode === 'timeLimit') {
      while (this.plan.length <= index) this.plan.push(...this.shuffled());
      return true;
    }
    return index < this.plan.length;
  }

  // ── 参照 ───────────────────────────────────────────────

  /** 現在の状態スナップショット。 */
  getState(): SessionState {
    const matcher = this.current?.matcher;
    const limitMs = this.timeLimitMs();
    const elapsedMs = (this.endedAt ?? this.now) - this.startedAt;
    return {
      phase: this.phase,
      difficulty: this.difficulty,
      score: this.score,
      combo: this.combo,
      maxCombo: this.maxCombo,
      comboBonus: calcComboBonus(this.combo, this.mods.comboBonusCap),
      gauge: this.gauge,
      gaugeMultiplier: calcGaugeMultiplier(this.gauge),
      performanceStage: this.stage,
      questionIndex: this.questionIndex,
      totalQuestions: this.totalQuestions(),
      completedQuestions: this.completedQuestions,
      word: this.current?.word ?? null,
      guide: matcher?.guide ?? '',
      typed: matcher?.typed ?? '',
      remaining: matcher?.remaining ?? '',
      nextKeys: matcher?.nextKeys ?? [],
      unitIndex: matcher?.unitIndex ?? 0,
      correctKeys: this.correctKeys,
      missKeys: this.missKeys,
      totalKeys: this.correctKeys + this.missKeys,
      accuracy: 1 - calcMissRate(this.correctKeys, this.missKeys),
      elapsedMs,
      remainingMs: limitMs === null ? null : Math.max(0, limitMs - elapsedMs),
      idleMs: Math.max(0, this.now - this.lastInputAt),
      missForgiveRemaining: this.forgiveRemaining,
    };
  }

  /**
   * リザルト。終了前に呼ぶと「今終わったら」の暫定値(cleared=false)を返す。
   */
  getResult(): SessionResult {
    return this.result ?? this.buildResult();
  }

  /** 難易度設定(UIのガイド表示制御用)。 */
  getDifficultySettings(): DifficultySettings {
    return getDifficultySettings(this.difficulty);
  }

  /** 合成済みアイテム効果。 */
  getModifiers(): ResolvedModifiers {
    return this.mods;
  }

  private buildResult(): SessionResult {
    const durationMs = Math.max(0, (this.endedAt ?? this.now) - this.startedAt);
    const missRate = calcMissRate(this.correctKeys, this.missKeys);
    const kpm = calcKpm(this.correctKeys, durationMs);
    const total = this.totalQuestions();
    const cleared =
      this.phase === 'ended' &&
      this.endReason !== 'aborted' &&
      (total === null
        ? this.completedQuestions > 0
        : total > 0 && this.completedQuestions >= total);
    const stars = calcStars({ cleared, missRate, kpm });
    return {
      difficulty: this.difficulty,
      score: this.score,
      maxCombo: this.maxCombo,
      correctKeys: this.correctKeys,
      missKeys: this.missKeys,
      totalKeys: this.correctKeys + this.missKeys,
      accuracy: 1 - missRate,
      missRate,
      kpm,
      durationMs,
      completedQuestions: this.completedQuestions,
      totalQuestions: total,
      cleared,
      stars,
      coins: cleared ? calcClearCoins(this.score, this.language, this.mods.coinMultiplier) : 0,
      endReason: this.endReason,
    };
  }
}
